/**
 * Where an EvidenceRef can be opened, and why it cannot (#966 AC1/AC2).
 *
 * Pure: no React, no i18n, no fetching. Two inputs only: what the backend STATES about the
 * source (#1195's `availability` / `availabilityReason`) and the client's own route table
 * (which kinds this app can open in-app). The client never infers navigability from the
 * claim, the id shape or the locator — that is the backend's contract to state.
 */
import { parsePositivePage } from '../../utils/sourceNavigation';
import type { AvailabilityReason, EvidenceKind, EvidenceRef } from '../../types/evidence';

/** The backend's three reasons plus the one thing only the client knows: its own routes. */
export const EVIDENCE_BLOCK_REASONS = [
  'permissionDenied',
  'sourceDeleted',
  'unsupportedLocator',
  'unsupportedSourceType',
] as const;
export type EvidenceBlockReason = typeof EVIDENCE_BLOCK_REASONS[number];

export type EvidenceJump =
  | { kind: 'link'; href: string; page: number | null }
  | { kind: 'blocked'; reason: EvidenceBlockReason };

/**
 * The client's route table: kinds whose source is a document this app opens at
 * `/documents/:id`. Mirrors the domain's `DOCUMENT_BACKED_KINDS`
 * (`domain/evidence/models.py`). `geometry_calculation` and `model_output` point at a CAD
 * model or a model run, which have no in-app destination until #967.
 */
const DOCUMENT_BACKED_KINDS: ReadonlySet<EvidenceKind> = new Set<EvidenceKind>([
  'document_page',
  'standard_clause',
  'past_trouble',
]);

const REASON_FROM_WIRE: Record<AvailabilityReason, EvidenceBlockReason> = {
  permission_denied: 'permissionDenied',
  source_deleted: 'sourceDeleted',
  unsupported_locator: 'unsupportedLocator',
};

/** The retrieval producer emits `page_numbers` (plural); the DRBFM producers emit the singular. */
const pageFrom = (locator: Record<string, unknown>): number | null => {
  const plural = locator.page_numbers;
  const candidate = locator.page_number ?? (Array.isArray(plural) ? plural[0] : undefined);
  return parsePositivePage(candidate as number | string | null | undefined);
};

/** Resolve one ref to a link or to a stated reason it cannot be opened. */
export const resolveEvidenceJump = (ref: EvidenceRef): EvidenceJump => {
  if (ref.availability === 'unavailable') {
    // The mapper refuses an unavailable ref without a reason (a contract error), so the
    // null branch is unreachable from the wire; the type still admits it, and the only
    // safe value for an impossible case is one that never links.
    return {
      kind: 'blocked',
      reason: ref.availabilityReason ? REASON_FROM_WIRE[ref.availabilityReason] : 'unsupportedLocator',
    };
  }
  if (!DOCUMENT_BACKED_KINDS.has(ref.evidenceKind)) {
    return { kind: 'blocked', reason: 'unsupportedSourceType' };
  }
  if (ref.sourceId === null) {
    // A withheld identity (#1198) only ever arrives on an unavailable ref, and the mapper
    // refuses the other combinations; if the type's own admission ever reached here, the
    // only safe value is one that never links — never `/documents/null`.
    return { kind: 'blocked', reason: 'unsupportedLocator' };
  }

  const page = pageFrom(ref.locator);
  // The id is not shape-checked here any more (availability is the backend's to state),
  // so it is encoded rather than interpolated: a hash, a `?` or a `../` must stay one
  // path segment under /documents/ and never rewrite the query or the route.
  const document = `/documents/${encodeURIComponent(ref.sourceId)}`;
  // Never `?page=<unparseable>`: `resolveInitialPage` turns that into page 1 with
  // `initialPageExplicit`, so the viewer opens the wrong page and never self-corrects.
  return page === null
    ? { kind: 'link', href: document, page: null }
    : { kind: 'link', href: `${document}?page=${page}`, page };
};
